import React, { CSSProperties, useEffect, useRef, useState } from "react";

interface TextInputProps {
  label?: string;
  name?: string;
  value?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  touched?: boolean;
  disabled?: boolean;
  Component?: string;
  maxLength?: number;
  allowDecimal?: boolean;
  limit?: [number, number];
  required?: boolean;
  className?: string;
  style?: CSSProperties;
  showAmountLabel?: boolean;
  currencyOptions?: { label: string; value: string }[];
  selectedCurrency?: string;
  onCurrencyChange?: (value: string) => void;
  currencyDisabled?: boolean;
}

const CurrencyInputWithType: React.FC<TextInputProps> = ({
  label,
  name,
  value,
  onChange,
  error,
  touched,
  disabled,
  Component = "",
  maxLength,
  allowDecimal = false,
  limit = [8, 2],
  required = false,
  className = "",
  style = {},
  showAmountLabel,
  currencyOptions,
  selectedCurrency,
  onCurrencyChange,
  currencyDisabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const [integerLimit, decimalLimit] = limit;

    const regex = allowDecimal
      ? new RegExp(`^\\d{0,${integerLimit}}(\\.\\d{0,${decimalLimit}})?$`)
      : new RegExp(`^\\d{0,${maxLength || integerLimit}}$`);

    if (regex.test(inputValue)) {
      onChange(e);
    }
  };

  const openDropdown = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();

    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      maxHeight: "200px",
      overflowY: "auto",
      background: "#fff",
      border: "1px solid #dee2e6",
      borderRadius: "4px",
    });

    setOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current?.contains(e.target as Node) ||
        triggerRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };

    const handleScroll = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  return (
    <div className={`mb-0 ${className}`} style={style}>
      <div className="input-group">
        {/* Input */}
        <div className="form-floating flex-grow-1">
          <input
            type="text"
            className={`form-control ${touched && error ? "is-invalid" : ""}`}
            id={`txt${Component}${name}`}
            name={name}
            value={value}
            onChange={handleChange}
            placeholder=" "
            disabled={disabled}
            maxLength={maxLength}
          />

          <label htmlFor={`txt${Component}${name}`}>
            {label}
            {!disabled && required && <span style={{ color: "red" }}>*</span>}
          </label>

          {touched && error && <div className="invalid-feedback">{error}</div>}
        </div>

        {/* Currency Trigger */}
        {showAmountLabel && currencyOptions && (
          <div
            ref={triggerRef}
            className="input-group-text position-relative"
            style={{
              cursor: disabled || currencyDisabled ? "not-allowed" : "pointer",
              minWidth: "80px",
              justifyContent: "space-between",
              opacity: currencyDisabled ? 0.6 : 1, // optional visual feedback
            }}
            onClick={() => !disabled && !currencyDisabled && openDropdown()}
          >
            {selectedCurrency || "USD"}

            <span
              className="material-symbols-outlined"
              style={{ fontSize: "16px" }}
            >
              keyboard_arrow_down
            </span>
          </div>
        )}
      </div>

      {/* Dropdown List */}
      {open && (
        <ul ref={dropdownRef} className="list-group" style={dropdownStyle}>
          {currencyOptions?.map((opt) => {
            const isSelected = opt.value === selectedCurrency;

            return (
              <li
                key={opt.value}
                className="list-group-item"
                style={{
                  cursor: "pointer",
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 10px",
                  backgroundColor: isSelected ? "#e9f3ff" : "#fff",
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onCurrencyChange?.(opt.value);
                  setOpen(false);
                }}
              >
                <input type="radio" checked={isSelected} readOnly />
                <span>{opt.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CurrencyInputWithType;
